// RPTLAYOUT1-F1-S1: the pure rules of the Reports home and results screens. No DOM, no requests. Totals, change figures and the
// Network total row are read from the server's result, never worked out here: this file only groups, sorts, labels and formats.
import {
  cellText,
  flat,
  initialCannedFilters,
  record,
  reportWords,
  type CannedCatalogDto,
  type CannedCatalogEntry,
  type CannedFilters,
  type CannedResultDto,
} from "./cannedReportModel.ts";
import { reportDescription, SAVED_DESCRIPTIONS } from "./reportCardDescriptions.ts";
import { drawingFor, niceAxis, type ChartModel, type ChartResult } from "./reportChartModel.ts";
import { isTechnicalKey, shortChartLabel } from "./reportTableModel.ts";

// ---------------------------------------------------------------------------------------------------------------------
// Home: cards, groups, search, favorites, recently run
// ---------------------------------------------------------------------------------------------------------------------

export const REPORT_GROUPS = [
  // RPTLAYOUT1-F1-S4: the Software Owner's own saved custom reports lead the page; no other console has a card in this group.
  { id: "custom-reports", label: "Custom reports" },
  { id: "sales", label: "Sales" },
  { id: "inventory", label: "Inventory" },
  { id: "purchasing", label: "Purchasing" },
  { id: "loss-prevention", label: "Loss prevention and payments" },
  { id: "staff-members", label: "Staff and members" },
  // RPTLAYOUT1-F1-S4: the Software Owner console's groups.
  { id: "platform-subscriptions", label: "Subscriptions and billing" },
  { id: "platform-ai-usage", label: "AI usage" },
  { id: "platform-onboarding", label: "Onboarding" },
  { id: "platform-royalties", label: "Royalties" },
] as const;

export interface ReportCard {
  id: string;
  title: string;
  group: string;
  /** The catalog says this person may run it now. A card that is not enabled is shown and cannot be opened. */
  enabled: boolean;
  /** Not enabled because the person lacks the source permission (the catalog's own flag), as opposed to hidden. */
  noPermission: boolean;
  /** A report outside the canned catalog, opened by the host (a legacy location report). */
  external: boolean;
  /** RPTMINE1: a saved custom report or a template, opened by the host in its existing screen. */
  saved?: SavedRef;
  /** RPTLAYOUT1-F1-S4: the plain reason this report cannot be built (nothing is recorded to build it from). The card is disabled and a circled ? holds the sentence. */
  reason?: string;
  /** RPTCARDS1: one or two plain sentences for the report's help, shown beside its title on the results page (RPTHOME-F1: never on the card). */
  help?: string;
  /** RPTHOME-F1: the catalog's full title when `title` is a shorter display label. It is the card's accessible name and tooltip. */
  fullTitle?: string;
  /** RPTHOME1-F2: the one-sentence description shown in the card's hover and focus tooltip and set as its aria-description. */
  description?: string;
  /** The small line under a saved card's title (its source and when it was last edited). */
  meta?: string;
}
/** RPTMINE1: what a saved card opens. `id` is the server's own definition or template id. */
export interface SavedRef {
  kind: "saved" | "template";
  id: string;
  section: SavedSectionId;
}
export type SavedSectionId = "mine" | "shared" | "templates";
export const SAVED_SECTIONS: ReadonlyArray<{ id: SavedSectionId; label: string }> = [
  { id: "mine", label: "My reports" },
  { id: "shared", label: "Shared with me" },
  { id: "templates", label: "Templates" },
];
export interface ExternalReportCard {
  id: string;
  title: string;
  group: string;
  help?: string;
}

/**
 * RPTCARDS1 (owner ruling 2026-10-20, DEC-481): the location console's older views that have no canned report. Each is a
 * card on the Reports home and opens the existing view. `permission` is the one the person must hold; nothing here names a role.
 * Usage and waste is retired (no card, no view), so it is absent on purpose. Royalty is kept: it holds the location dispute and charges export (DEC-477).
 */
export const LOCATION_VIEW_CARDS: ReadonlyArray<ExternalReportCard & { permission: string }> = [
  {
    id: "sales-by-day",
    title: "Sales by day",
    group: "sales",
    permission: "report.sales_by_day.view",
    help: "Net sales, tenders, tips and discounts for each day, tied to the closeout. Pick the dates inside the report.",
  },
  {
    id: "cost-margin",
    title: "Cost and margin",
    group: "inventory",
    permission: "report.cost_margin.view",
    help: "What each drink costs to make against what it sells for, with waste and count shortfall shown beside it.",
  },
  {
    id: "abc-spirits",
    title: "Spirits used vs sold (ABC)",
    group: "inventory",
    permission: "report.abc_spirits.view",
    help: "Bottles used against drinks sold for a month, by brand and bottle size, in the layout your state ABC board asks for. It never files for you.",
  },
  {
    id: "sales-tax",
    title: "Sales tax",
    group: "sales",
    permission: "report.sales_tax.view",
    help: "A worksheet of the tax collected for a month, quarter or year, to help you file. It never files for you.",
  },
  {
    id: "menu-profitability",
    title: "Menu profitability",
    group: "sales",
    permission: "report.menu_profitability.view",
    help: "How much each drink earns against your target, so you can see which to promote and which to reprice.",
  },
  {
    id: "royalty-statement",
    title: "Royalty",
    group: "sales",
    permission: "report.sales_by_day.view",
    help: "This location's royalty for a month, its charges and adjustments. A location Admin can dispute a charge and export the ledger here.",
  },
];

/**
 * RPTHOME-F1: shorter DISPLAY labels for the long canned report titles. Only the label on the card and the results page
 * changes; ids, routes, saved reports and exports keep the catalog's own title.
 */
const DISPLAY_TITLES: Readonly<Record<string, string>> = {
  "Recorded transfer quantities": "Transfers",
  "Recorded waste and reversal activity by reason": "Waste by reason",
  "Recorded preparation stock movements": "Prep stock movements",
  "Recorded closed-count comparison": "Count comparison",
  "Recorded closed-count item details": "Count item details",
  "Closed count-session summary": "Count summary",
  "Current active-item inventory value": "Inventory value",
  "Current open purchase orders, created in period": "Open purchase orders",
  "Network current open orders, created in period": "Network open orders",
  "Received spend by vendor": "Spend by vendor",
  "Received-delivery cost": "Delivery cost",
  "Payments by method (gross source totals)": "Payments by method",
  "Recorded ticket price-change commands": "Ticket price changes",
  "Walkouts (closeout-defined metrics)": "Walkouts",
  "Paid in and paid out": "Paid in and out",
  "Discount application activity": "Discounts applied",
  "Daily loss prevention activity": "Daily loss prevention",
  "Employee preparation and void activity": "Employee prep and voids",
  "Prepared voids by reason and employee": "Prepared voids",
  "Recorded guest-ticket activity": "Guest tickets",
  "Recorded refunds": "Refunds",
  "Recorded tips by employee and day": "Tips by employee",
};
function descriptionFor(id: string): { description?: string } {
  const description = reportDescription(id);
  return description === undefined ? {} : { description };
}
export function displayTitle(title: string): string {
  return DISPLAY_TITLES[title] ?? title;
}

/** The location view cards this person may open, given a permission check. */
export function locationViewCards(has: (permission: string) => boolean): ExternalReportCard[] {
  return LOCATION_VIEW_CARDS.filter((card) => has(card.permission)).map((card) => ({ id: card.id, title: card.title, group: card.group, ...(card.help === undefined ? {} : { help: card.help }) }));
}

/**
 * The cards the home shows. A catalog entry that is planned, blocked or has no recorded source (Labor, Sales by hour) is
 * not a card: nothing is invented for a report that cannot run. An entry refused for a missing source permission stays as a
 * disabled card so the person can see it exists. The labels are the catalog's own, never a role name.
 */
export function catalogCards(
  entries: readonly CannedCatalogEntry[],
  external: readonly ExternalReportCard[] = [],
): ReportCard[] {
  const cards: ReportCard[] = [];
  for (const entry of entries) {
    const noPermission = !entry.canRun && entry.unavailableReason === "source_permission_required";
    // A report with nothing recorded to build it from is listed with its reason, never faked and never hidden.
    if (!entry.canRun && !noPermission && entry.plainReason === undefined) continue;
    cards.push({
      ...(entry.canRun || entry.plainReason === undefined ? {} : { reason: entry.plainReason }),
      id: entry.id,
      title: displayTitle(entry.title),
      ...(displayTitle(entry.title) === entry.title ? {} : { fullTitle: entry.title }),
      ...descriptionFor(entry.id),
      group: entry.group,
      enabled: entry.canRun,
      noPermission,
      external: false,
    });
  }
  for (const card of external) {
    if (cards.some((existing) => existing.id === card.id)) continue;
    cards.push({ ...card, ...descriptionFor(card.id), enabled: true, noPermission: false, external: true });
  }
  return cards;
}

export interface CardGroup {
  id: string;
  label: string;
  cards: ReportCard[];
}
/** Cards under their headed groups in the approved order, filtered by a search of the title. Empty groups are dropped. */
export function groupedCards(allCards: readonly ReportCard[], query: string): CardGroup[] {
  const needle = query.trim().toLowerCase();
  const matches = (card: ReportCard) => needle === "" || card.title.toLowerCase().includes(needle);
  // Saved reports and templates have their own headed groups (savedGroups); this is the catalog and the host's own views.
  const cards = allCards.filter((card) => card.saved === undefined);
  // RPTHOME-F1: every section sorts the same way, alphabetical by display title, whatever supplied the card.
  const byTitle = (a: ReportCard, b: ReportCard) => a.title.localeCompare(b.title, "en", { sensitivity: "base" });
  const known = new Set<string>(REPORT_GROUPS.map((group) => group.id));
  const groups: CardGroup[] = REPORT_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    cards: cards.filter((card) => card.group === group.id && matches(card)).sort(byTitle),
  }));
  const others = [...new Set(cards.filter((card) => !known.has(card.group)).map((card) => card.group))];
  for (const id of others)
    groups.push({ id, label: reportWords(id), cards: cards.filter((card) => card.group === id && matches(card)).sort(byTitle) });
  return groups.filter((group) => group.cards.length > 0);
}

// ---------------------------------------------------------------------------------------------------------------------
// RPTMINE1: My reports, Shared with me, Templates
// ---------------------------------------------------------------------------------------------------------------------

/** The key a saved report or template is starred and recorded under. The marks routes take lowercase ids with hyphens. */
export function savedMarkKey(kind: SavedRef["kind"], id: string): string {
  return `${kind}-${id.toLowerCase()}`;
}
/** The saved-report list bodies of both consoles carry these fields (the location also carries `visibility`). */
interface SavedItemShape {
  id: string;
  title: string;
  mine: boolean;
  datasetLabel: string;
  editedAt: string;
  editedBy?: { name: string };
}
interface TemplateItemShape {
  id: string;
  title: string;
  datasetLabel: string;
  versionNumber: number;
}
const SAVED_ID = /^[A-Za-z0-9-]{1,64}$/;
function isSavedItem(value: unknown): value is SavedItemShape {
  return (
    record(value) &&
    typeof value["id"] === "string" &&
    SAVED_ID.test(value["id"]) &&
    typeof value["title"] === "string" &&
    value["title"] !== "" &&
    typeof value["mine"] === "boolean" &&
    typeof value["datasetLabel"] === "string" &&
    typeof value["editedAt"] === "string"
  );
}
function isTemplateItem(value: unknown): value is TemplateItemShape {
  return (
    record(value) &&
    typeof value["id"] === "string" &&
    SAVED_ID.test(value["id"]) &&
    typeof value["title"] === "string" &&
    value["title"] !== "" &&
    typeof value["datasetLabel"] === "string" &&
    typeof value["versionNumber"] === "number"
  );
}
export function savedItems(body: unknown): SavedItemShape[] | undefined {
  return record(body) && Array.isArray(body["items"]) && body["items"].every(isSavedItem) ? (body["items"] as SavedItemShape[]) : undefined;
}
export function templateItems(body: unknown): TemplateItemShape[] | undefined {
  return record(body) && Array.isArray(body["items"]) && body["items"].every(isTemplateItem) ? (body["items"] as TemplateItemShape[]) : undefined;
}
/** "Oct 14" for a saved report's last edit; blank when the date does not read. */
function editedText(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
/**
 * The saved cards. A report is "mine" when the server says it is, and "shared with me" otherwise: the list holds only reports this
 * person may open, so one that is not theirs reached them through a share. Every card is enabled, because the list is already the
 * server's permission-filtered answer; a card that cannot run says so when it opens.
 */
export function savedCards(
  saved: readonly SavedItemShape[] | undefined,
  templates: readonly TemplateItemShape[] | undefined,
): ReportCard[] {
  const cards: ReportCard[] = [];
  for (const item of saved ?? []) {
    const section: SavedSectionId = item.mine ? "mine" : "shared";
    const edited = editedText(item.editedAt);
    const by = !item.mine && item.editedBy !== undefined ? ` by ${item.editedBy.name}` : "";
    cards.push({
      id: savedMarkKey("saved", item.id),
      title: item.title,
      group: section,
      enabled: true,
      noPermission: false,
      external: false,
      description: SAVED_DESCRIPTIONS.saved,
      saved: { kind: "saved", id: item.id, section },
      meta: [item.datasetLabel, edited === "" ? "" : `Edited ${edited}${by}`].filter((part) => part !== "").join(", "),
    });
  }
  for (const item of templates ?? []) {
    cards.push({
      id: savedMarkKey("template", item.id),
      title: item.title,
      group: "templates",
      enabled: true,
      noPermission: false,
      external: false,
      description: SAVED_DESCRIPTIONS.template,
      saved: { kind: "template", id: item.id, section: "templates" },
      meta: `${item.datasetLabel}, version ${item.versionNumber}`,
    });
  }
  return cards;
}
export interface SavedGroup {
  id: SavedSectionId;
  label: string;
  cards: ReportCard[];
}
/** The three saved groups in the approved order, filtered by the same title search as the canned cards. Empty groups are dropped. */
export function savedGroups(cards: readonly ReportCard[], query: string): SavedGroup[] {
  const needle = query.trim().toLowerCase();
  return SAVED_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    cards: cards
      .filter((card) => card.saved?.section === section.id)
      .filter((card) => needle === "" || card.title.toLowerCase().includes(needle)),
  })).filter((group) => group.cards.length > 0);
}

export interface ReportMarks {
  favorites: string[];
  recent: Array<{ reportId: string; at: string; label: string }>;
}
export const NO_MARKS: ReportMarks = { favorites: [], recent: [] };
export const RECENT_LIMIT = 8;

export function isMarks(value: unknown): value is ReportMarks {
  return (
    record(value) &&
    Array.isArray(value["favorites"]) &&
    value["favorites"].every((id) => typeof id === "string") &&
    Array.isArray(value["recent"]) &&
    value["recent"].every(
      (run) =>
        record(run) &&
        typeof run["reportId"] === "string" &&
        typeof run["at"] === "string" &&
        typeof run["label"] === "string",
    )
  );
}
export function isFavorite(marks: ReportMarks, id: string): boolean {
  return marks.favorites.includes(id);
}
export function withFavorite(marks: ReportMarks, id: string, on: boolean): ReportMarks {
  const rest = marks.favorites.filter((existing) => existing !== id);
  return { ...marks, favorites: on ? [...rest, id].sort() : rest };
}
export function withRun(marks: ReportMarks, id: string, label: string, at: string): ReportMarks {
  const rest = marks.recent.filter((run) => run.reportId !== id);
  return { ...marks, recent: [{ reportId: id, at, label }, ...rest].slice(0, RECENT_LIMIT) };
}
/** Favorite cards in the catalog's own order, only those this person can still open. */
export function favoriteCards(cards: readonly ReportCard[], marks: ReportMarks): ReportCard[] {
  return cards.filter((card) => card.enabled && marks.favorites.includes(card.id));
}
export interface RecentRow {
  card: ReportCard;
  label: string;
  when: string;
}
/** Recent runs, newest first, only for reports this person can open now (a report that lost its permission drops out). */
export function recentRows(cards: readonly ReportCard[], marks: ReportMarks, now: Date): RecentRow[] {
  const rows: RecentRow[] = [];
  for (const run of [...marks.recent].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))) {
    const card = cards.find((candidate) => candidate.id === run.reportId && candidate.enabled);
    if (card !== undefined) rows.push({ card, label: run.label, when: whenText(run.at, now) });
  }
  return rows;
}
const dayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
/** "Today 9:05 AM", "Yesterday", "Mon" within the week, else "Oct 3". Local time of this browser. */
export function whenText(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const days = Math.round((dayStart(now) - dayStart(at)) / 86_400_000);
  if (days === 0)
    return `Today ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  if (days === 1) return "Yesterday";
  if (days > 1 && days < 7) return at.toLocaleDateString("en-US", { weekday: "short" });
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------------------------------------------------
// Dates and the one-line summary on the control row
// ---------------------------------------------------------------------------------------------------------------------

export const DATE_CHOICES: ReadonlyArray<{ preset: string; label: string }> = [
  { preset: "today", label: "Today" },
  { preset: "yesterday", label: "Yesterday" },
  { preset: "last_7_days", label: "Last 7 days" },
  { preset: "last_30_days", label: "Last 30 days" },
  { preset: "this_month", label: "This month" },
  { preset: "last_month", label: "Last month" },
  { preset: "last_quarter", label: "Last quarter" },
  { preset: "year_to_date", label: "This year" },
  { preset: "last_year", label: "Last year" },
];
/** The presets one tap away: the nine of the design that this report's catalog entry also offers. */
export function presetChoices(entry: CannedCatalogEntry): Array<{ preset: string; label: string }> {
  const offered = entry.datePresets ?? [];
  return DATE_CHOICES.filter((choice) => offered.includes(choice.preset));
}
export function presetLabel(preset: string): string {
  return DATE_CHOICES.find((choice) => choice.preset === preset)?.label ?? reportWords(preset);
}
const monthDay = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};
export function datesLabel(filters: Pick<CannedFilters, "preset" | "from" | "to">): string {
  if (filters.preset === "custom")
    return filters.from !== "" && filters.to !== "" ? `${monthDay(filters.from)} to ${monthDay(filters.to)}` : "Custom range";
  return presetLabel(filters.preset);
}
/** "Last 30 days, vs prior" on the Dates selector. */
export function datesSummary(filters: CannedFilters): string {
  return `${datesLabel(filters)}${filters.compare !== "" ? ", vs prior" : ""}`;
}
/**
 * The dates and chart a report opens with when this person has not run it yet: Last 30 days when the report offers it, else its
 * first preset; the by-day chart when the report offers one. The location scope is the host's, as initialCannedFilters has it.
 */
export function startingFilters(entry: CannedCatalogEntry, catalog: CannedCatalogDto): CannedFilters {
  const initial = initialCannedFilters(entry, catalog);
  // MGRDAYMAJ1 (manager-day-09, 10): a report that lists one row per day answers "how did yesterday go", so it opens on the last
  // week, not the last month. The Dates menu still offers every range.
  const preset =
    DAY_BY_DAY_REPORTS.has(entry.id) && entry.datePresets?.includes("last_7_days") === true
      ? "last_7_days"
      : entry.datePresets?.includes("last_30_days") === true
        ? "last_30_days"
        : initial.preset;
  return {
    ...initial,
    preset,
    chartBasis: entry.chartBases?.includes("business_date") === true ? "business_date" : initial.chartBasis,
  };
}
export function compareKind(entry: CannedCatalogEntry): string {
  return entry.compareKinds?.[0] ?? "";
}
export function withCompare(entry: CannedCatalogEntry, filters: CannedFilters, on: boolean): CannedFilters {
  return { ...filters, compare: on ? compareKind(entry) : "" };
}
/** What the scope picks from. The Software Owner reads across customers, every other console across locations. */
export interface ScopeWords {
  title: string;
  singular: string;
  plural: string;
}
export const LOCATION_WORDS: ScopeWords = { title: "Locations", singular: "location", plural: "locations" };
export const CUSTOMER_WORDS: ScopeWords = { title: "Customers", singular: "customer", plural: "customers" };
export function scopeLabel(filters: CannedFilters, catalog: CannedCatalogDto, words: ScopeWords = LOCATION_WORDS): string {
  if (catalog.scope.kind === "fixed_location") return catalog.scope.locations[0]?.name ?? "Location";
  if (filters.allLocations) return `All ${words.plural}`;
  if (filters.locationIds.length === 1)
    return catalog.scope.locations.find((site) => site.id === filters.locationIds[0])?.name ?? `1 ${words.singular}`;
  return `${filters.locationIds.length} ${words.plural}`;
}
/** The label kept with a Recently run row: "Last 30 days, all locations" (a location console names no scope). */
export function runLabel(filters: CannedFilters, catalog: CannedCatalogDto, words: ScopeWords = LOCATION_WORDS): string {
  // A snapshot report has no dates: it reads the platform as it is now.
  const dates = filters.preset === "" ? "As of today" : datesLabel(filters);
  return catalog.scope.kind === "fixed_location" ? dates : `${dates}, ${scopeLabel(filters, catalog, words).toLowerCase()}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Results: the table, its pinned total row, the totals card
// ---------------------------------------------------------------------------------------------------------------------

export interface TableColumn {
  key: string;
  label: string;
  numeric: boolean;
  /** A change column: shown as a chip with an arrow and a sign. */
  change: boolean;
}
export interface TableRow {
  values: Record<string, unknown>;
  text: Record<string, string>;
}
export interface ResultTable {
  columns: TableColumn[];
  rows: TableRow[];
  /** Built only from the server's `totals`. Absent when the server sent none that matches a column. */
  total: { label: string; text: Record<string, string>; values: Record<string, unknown> } | null;
}
const LABELS: Readonly<Record<string, string>> = {
  customerName: "Customer",
  planName: "Plan",
  networkName: "Franchisor network",
  mrrCents: "Monthly recurring revenue",
  locationName: "Location",
  franchisee: "Franchisee",
  businessDate: "Date",
  label: "Name",
  // UIFIX1 (SF-4): plain headings. A heading never says "cents" over a dollar figure, and the server's field names read as words.
  gross_cents: "Gross sales",
  discount_cents: "Discounts",
  comp_cents: "Comps",
  refund_before_tax_cents: "Refunds before tax",
  net_cents: "Net sales",
  tax_cents: "Tax",
  tips_cents: "Tips",
  void_count: "Voids",
  void_value_cents: "Void value",
  walkout_count: "Walkouts",
  walkout_owed_cents: "Walkout owed",
  walkoutCount: "Walkouts",
  walkoutOwedCents: "Walkout owed",
  finalized: "Day closed",
  itemName: "Item",
  categoryLabel: "Category",
  hierarchyLabel: "Category",
  recordedVendorName: "Vendor",
  vendorLabel: "Vendor",
  createdBusinessDate: "Created",
  placedBusinessDate: "Placed",
  employeeName: "Employee",
  recordedStatus: "Status",
  coverage: "Status",
  state: "Status",
  status: "Status",
  clientKind: "Guest type",
  currentMember: "Member",
  guestTicketAssociations: "Guest tickets",
  currentNonremovedLineQuantity: "Drinks",
  recordedPreparationQuantity: "Drinks prepared",
  preparedVoidEvents: "Prepared voids",
  recordedTipsCents: "Tips",
  positivePaymentContributions: "Tipped payments",
  depletionQuantity: "Used",
  returnedQuantity: "Returned",
  netDepletionQuantity: "Net used",
  quantityUnit: "Unit",
  recordedReference: "Item",
  postingCount: "Entries",
  weekday: "Day",
  week: "Week",
  recordedValuedSubtotalCents: "Recorded value",
  completeValueCents: "Complete value",
  deliveredSubtotalCents: "Delivered",
  correctedSubtotalCents: "Corrected",
  correctionSubtotalCents: "Correction",
  completeCorrectedTotalCents: "Complete total",
  correctedChangeBp: "Change",
  netChangeBp: "Net change",
};
const DROPPED = [/^prior$/, /^prior\./, /^priorCoverage(\.|$)/, /^coverage(\.|$)/, /^comparisonStatus$/, /^priorBusinessDate$/, /^returnedLocations$/, /^expectedLocations$/,
  // RPTHOME-F1: internal columns (cohort index, date counts, availability flags) are not shown; the CSV export still has them.
  /(^|\.)weekdayIndex$/, /(^|\.)[a-z]*DateCount$/i, /(^|\.)[a-z]*Availability$/i,
];
export function columnLabel(key: string): string {
  if (key.startsWith("changeBp.")) return "vs prior";
  const last = key.replace(/^current\./, "").split(".").pop() ?? key;
  if (LABELS[last] !== undefined) return LABELS[last] as string;
  const words = reportWords(last.replace(/(Cents|Bp|_cents|_bp)$/, "") || last);
  return words.charAt(0) + words.slice(1).toLowerCase();
}
/** The change column worth showing: the net figure when the server sent one, else the first. Null when no row has a value. */
function changeKey(keys: readonly string[], cells: ReadonlyArray<Record<string, unknown>>): string | null {
  const candidates = keys.filter((key) => key.startsWith("changeBp."));
  const populated = candidates.filter((key) => cells.some((row) => typeof row[key] === "number"));
  return populated.find((key) => key === "changeBp.netCents") ?? populated[0] ?? null;
}
export function resultTable(result: CannedResultDto): ResultTable {
  const cells = result.rows.map((row) => flat(row));
  const all = [...new Set(cells.flatMap((row) => Object.keys(row)))];
  const change = changeKey(all, cells);
  const kept = all.filter(
    (key) => !DROPPED.some((pattern) => pattern.test(key)) && (!key.startsWith("changeBp.") || key === change),
  );
  const isText = (key: string) =>
    cells.some((row) => row[key] !== null && row[key] !== undefined) &&
    cells.every((row) => row[key] === null || row[key] === undefined || typeof row[key] === "string");
  const textKeys = kept.filter((key) => isText(key) && !key.startsWith("changeBp."));
  const numberKeys = kept.filter((key) => !textKeys.includes(key) && key !== change);
  const ordered = [...textKeys, ...numberKeys, ...(change === null ? [] : [change])];
  const columns: TableColumn[] = ordered.map((key) => ({
    key,
    label: columnLabel(key),
    numeric: !textKeys.includes(key),
    change: key === change,
  }));
  const rows: TableRow[] = cells.map((row) => ({
    values: row,
    text: Object.fromEntries(columns.map((column) => [column.key, cellText(row[column.key], column.key)])),
  }));
  const totals = record(result["totals"]) ? flat(result["totals"]) : null;
  let total: ResultTable["total"] = null;
  if (totals !== null) {
    const matched = columns.filter((column) => column.numeric && totals[column.key] !== undefined && totals[column.key] !== null);
    if (matched.length > 0) {
      const first = columns.find((column) => !column.numeric);
      const label = result.scope.kind === "network" ? "Network total" : "Total";
      const text: Record<string, string> = {};
      const values: Record<string, unknown> = {};
      for (const column of matched) {
        text[column.key] = cellText(totals[column.key], column.key);
        values[column.key] = totals[column.key];
      }
      if (first !== undefined) text[first.key] = label;
      total = { label, text, values };
    }
  }
  return { columns, rows, total };
}

/**
 * Nothing to show: the server sent no rows, or every figure in every row is zero or missing. A day with $0.00 of sales reads as
 * nothing recorded, not as a table of zeros. A result with no figure columns at all (a list of names) is empty only with no rows.
 */
export function isEmptyResult(result: CannedResultDto): boolean {
  if (result.rows.length === 0) return true;
  const table = resultTable(result);
  const figures = table.columns.filter((column) => column.numeric && !column.change);
  if (figures.length === 0) return false;
  return table.rows.every((row) => figures.every((column) => {
    const value = row.values[column.key];
    return value === null || value === undefined || value === 0;
  }));
}

/** One person's recorded card tips across the dates on screen (CLOSEFIX1, manager-day-01). */
export interface OwedPerson {
  key: string;
  name: string;
  locationName: string;
  cents: number;
}
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
/**
 * Tips by employee per person. Every figure is the server's own per-person rollup (`employees`), which it adds up from the same
 * daily rows it sends, and the totals are checked here against both the daily rows on screen and the report's own total.
 * Null (and a line on the console) when any check fails or the rows were cut short, so a wrong total is never drawn.
 */
export function owedPerPerson(result: CannedResultDto): OwedPerson[] | null {
  const employees = result["employees"];
  const totals = result["totals"];
  if (!Array.isArray(employees) || !isRecord(totals) || typeof totals["recordedTipsCents"] !== "number") return null;
  const names = new Map(result.scope.locations.map((site) => [site.id, site.name]));
  const people: OwedPerson[] = [];
  for (const entry of employees) {
    if (!isRecord(entry) || !isRecord(entry["totals"])) return null;
    const cents = entry["totals"]["recordedTipsCents"];
    const locationId = entry["locationId"];
    const employeeId = entry["employeeId"];
    const name = entry["employeeName"];
    if (typeof cents !== "number" || typeof locationId !== "string" || typeof employeeId !== "string" || typeof name !== "string") return null;
    people.push({ key: JSON.stringify([locationId, employeeId]), name, locationName: names.get(locationId) ?? "", cents });
  }
  const sum = people.reduce((total, person) => total + person.cents, 0);
  if (sum !== totals["recordedTipsCents"]) {
    console.error("Tips by employee: per-person totals do not add up to the report total; per-person view not drawn.");
    return null;
  }
  if (result.display.truncated !== true) {
    const daily = new Map<string, number>();
    for (const row of result.rows) {
      const key = JSON.stringify([row["locationId"], row["employeeId"]]);
      daily.set(key, (daily.get(key) ?? 0) + (typeof row["recordedTipsCents"] === "number" ? row["recordedTipsCents"] : 0));
    }
    if (daily.size !== people.length || people.some((person) => daily.get(person.key) !== person.cents)) {
      console.error("Tips by employee: per-person totals do not match the daily rows; per-person view not drawn.");
      return null;
    }
  }
  return people.sort((a, b) => b.cents - a.cents || a.name.localeCompare(b.name));
}

export interface PayPeriodChoice {
  from: string;
  to: string;
  label: string;
}
/** Pay periods as date choices: a period still running ends today at the latest, and one that starts after today is left out. */
export function payPeriodChoices(
  periods: ReadonlyArray<{ periodStart: string; periodEnd: string }>,
  today: string,
): PayPeriodChoice[] {
  return periods
    .filter((period) => period.periodStart <= today && period.periodEnd >= period.periodStart)
    .map((period) => {
      const to = period.periodEnd > today ? today : period.periodEnd;
      return { from: period.periodStart, to, label: datesLabel({ preset: "custom", from: period.periodStart, to: period.periodEnd }) };
    });
}

export type SortDirection = "asc" | "desc";
export interface TableSort {
  key: string;
  dir: SortDirection;
}
/** A new header tap sorts descending for figures and ascending for words; the same header again flips it. */
export function nextSort(table: ResultTable, current: TableSort | null, key: string): TableSort {
  if (current?.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  const column = table.columns.find((candidate) => candidate.key === key);
  return { key, dir: column?.numeric === true ? "desc" : "asc" };
}
/** MGRDAYMAJ1 (manager-day-09): the reports that list one row per business day. They open newest first, so yesterday is the first
 * row, and a person who taps the Date heading gets the oldest first order back. */
const DAY_BY_DAY_REPORTS: ReadonlySet<string> = new Set(["daily-sales", "loss-prevention-trends", "tips-by-employee-day"]);
export function defaultSort(reportId: string, table: ResultTable): TableSort | null {
  return DAY_BY_DAY_REPORTS.has(reportId) && table.columns.some((column) => column.key === "businessDate")
    ? { key: "businessDate", dir: "desc" }
    : null;
}
/** Rows ordered by one column. A missing value sorts last in both directions. The total row is never part of the sort. */
export function sortRows(table: ResultTable, sort: TableSort | null): TableRow[] {
  if (sort === null) return table.rows;
  const factor = sort.dir === "asc" ? 1 : -1;
  return table.rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = a.row.values[sort.key];
      const right = b.row.values[sort.key];
      const leftMissing = left === null || left === undefined;
      const rightMissing = right === null || right === undefined;
      if (leftMissing || rightMissing) return leftMissing === rightMissing ? a.index - b.index : leftMissing ? 1 : -1;
      const order =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right), "en-US", { numeric: true, sensitivity: "base" });
      return order === 0 ? a.index - b.index : order * factor;
    })
    .map((entry) => entry.row);
}

export interface TotalFigure {
  key: string;
  label: string;
  text: string;
  change: { text: string; direction: "up" | "down" | "flat" } | null;
}
const FIGURE_PRIORITY = ["grossCents", "discountsCents", "netCents", "ticketCount", "tickets"];
export function changeChip(basisPoints: unknown): TotalFigure["change"] {
  if (typeof basisPoints !== "number" || !Number.isFinite(basisPoints)) return null;
  const direction = basisPoints > 0 ? "up" : basisPoints < 0 ? "down" : "flat";
  const magnitude = (Math.abs(basisPoints) / 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return { text: `${direction === "up" ? "▲" : direction === "down" ? "▼" : "▬"} ${magnitude}%`, direction };
}
/** Up to four headline figures from the server's totals, each with the server's own change when one was sent. */
export function totalFigures(result: CannedResultDto): TotalFigure[] {
  const source = ["totals", "summary"].map((key) => result[key]).find(record);
  if (!record(source)) return [];
  const cells = flat(source);
  const change = record(source["changeBp"]) ? source["changeBp"] : {};
  const figures: TotalFigure[] = [];
  for (const [key, value] of Object.entries(cells)) {
    if (typeof value !== "number" || /(^|\.)(prior|coverage|priorCoverage|changeBp)(\.|$)/.test(key) || /Bp$/.test(key) || isTechnicalKey(key)) continue;
    const metric = key.replace(/^current\./, "");
    if (metric.includes(".")) continue;
    figures.push({
      key,
      label: columnLabel(key),
      text: cellText(value, key),
      change: changeChip(change[metric]),
    });
  }
  const rank = (figure: TotalFigure) => {
    const index = FIGURE_PRIORITY.indexOf(figure.key.replace(/^current\./, ""));
    return index === -1 ? FIGURE_PRIORITY.length : index;
  };
  return figures
    .map((figure, index) => ({ figure, index }))
    .sort((a, b) => rank(a.figure) - rank(b.figure) || a.index - b.index)
    .slice(0, 4)
    .map((entry) => entry.figure);
}

// ---------------------------------------------------------------------------------------------------------------------
// The chart: the server's points, in the model the CRB3 renderer draws
// ---------------------------------------------------------------------------------------------------------------------

const NOT_A_MEASURE = new Set(["returnedLocations", "expectedLocations", "shareBp", "priorShareBp", "prior", "priorKey"]);
/** The chart model for a result's own points, or undefined when there is nothing faithful to draw (the card is then omitted). */
export function chartModel(result: CannedResultDto, title: string): ChartResult | undefined {
  const chart = result["chart"];
  if (!record(chart) || !Array.isArray(chart["points"]) || chart["truncated"] === true || chart["availability"] === "unavailable")
    return undefined;
  const points = chart["points"].filter(record);
  if (points.length === 0) return undefined;
  const numeric = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;
  // RPTLAYOUT1-F1-S4: a result may name several measures to draw side by side (cost beside billed, due beside collected).
  const named = Array.isArray(chart["series"])
    ? chart["series"].filter((key): key is string => typeof key === "string" && points.some((point) => numeric(point[key]) !== null))
    : [];
  const measure =
    named[0] ??
    (points.some((point) => numeric(point["current"]) !== null)
      ? "current"
      : Object.keys(points[0] ?? {}).find(
          (key) => !NOT_A_MEASURE.has(key) && points.some((point) => numeric(point[key]) !== null),
        ));
  if (measure === undefined) return undefined;
  const { type, limit } = drawingFor(chart["type"]);
  if (points.length > limit) return undefined;
  const money = chart["unit"] === "money" || chart["unit"] === "USD_cents" || /cents/i.test(measure) || measure === "current";
  // A date reads "Sep 21" on the axis, not "2026-09-21": thirty of them in a row must not run into each other.
  const label = (point: Record<string, unknown>, index: number) => {
    const text = String(point["label"] ?? point["businessDate"] ?? point["date"] ?? point["month"] ?? index + 1);
    // UIFIX1 (SF-2): short names on the axis (the item, the vendor, the date), never the location prefix the scope already says.
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? monthDay(text) : shortChartLabel(text, result.scope.locations.map((site) => site.name));
  };
  const series = (named.length > 1 ? named : [measure]).map((key) => ({ key, label: key === "current" ? "This period" : columnLabel(key) })).map((entry) => {
    const values = points.map((point) => numeric(point[entry.key]));
    return { ...entry, values, total: values.reduce<number>((sum, value) => sum + (value ?? 0), 0) };
  });
  const prior = measure === "current" && points.some((point) => numeric(point["prior"]) !== null);
  if (prior) {
    const values = points.map((point) => numeric(point["prior"]));
    series.push({ key: "prior", label: "Prior period", values, total: values.reduce<number>((sum, value) => sum + (value ?? 0), 0) });
  }
  const every = series.flatMap((entry) => entry.values).filter((value): value is number => value !== null);
  if (every.length === 0) return undefined;
  const model: ChartModel = {
    type,
    title,
    unit: money ? "money" : "number",
    categories: points.map(label),
    series,
    total: series.reduce((sum, entry) => sum + entry.total, 0),
    axis: niceAxis(Math.min(...every), Math.max(...every)),
    shares: [],
  };
  return { ok: true, model };
}

/** The error sentence for a refused or failed run, kept apart from the screen so the status mapping is testable. */
export type RunFailure = "denied" | "busy" | "error";
export function runFailure(status: number): RunFailure {
  return status === 401 || status === 403 ? "denied" : status === 409 ? "busy" : "error";
}
